// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/services/sapori/FlavorArt.tsx
// 🔧 PURPOSE: Le bolle di "Spara Sapori", un SVG per sapore, usato sia nella
//   pagina (img) sia sul tavolo (canvas, vedi SaporiCanvas). Ogni sapore ha
//   il suo colore E il suo simbolo, così si distinguono anche senza colori:
//   - limone: lo spicchio (la ruota);
//   - oliva: l'oliva con la foglia;
//   - pomodorino: la stella del picciolo;
//   - mare: le onde;
//   - fico: la goccia con i semini;
//   - burrata: il fagotto con il nodo verde.
// ============================================

import React from 'react';
import type { Flavor } from './engine';

export interface FlavorStyle {
  light: string;
  base: string;
  dark: string;
}

export const FLAVOR_STYLE: Record<Flavor, FlavorStyle> = {
  lemon: { light: '#FEF9C3', base: '#FACC15', dark: '#A16207' },
  olive: { light: '#D9E7A6', base: '#7C9A35', dark: '#3A4F14' },
  tomato: { light: '#FECACA', base: '#E53935', dark: '#7F1D1D' },
  sea: { light: '#BAE6FD', base: '#0EA5E9', dark: '#0C4A6E' },
  fig: { light: '#E9D5FF', base: '#9333EA', dark: '#3B0764' },
  burrata: { light: '#FFFFFF', base: '#F3EBDD', dark: '#9C8763' },
};

const ICONS: Record<Flavor, string> = {
  lemon:
    '<circle cx="32" cy="33" r="14" fill="#FFFBEB" stroke="#B45309" stroke-width="2"/>' +
    '<circle cx="32" cy="33" r="10.5" fill="#FDE68A"/>' +
    '<g stroke="#FFFBEB" stroke-width="1.8" stroke-linecap="round">' +
    '<path d="M32 33V22.5M32 33l9.1-5.25M32 33l9.1 5.25M32 33v10.5M32 33l-9.1 5.25M32 33l-9.1-5.25"/></g>',
  olive:
    '<path d="M36 21c6-6 13-6 15-5-1 6-6 10-13 9" fill="#F0F7D4" stroke="#2F4216" stroke-width="1.6" stroke-linejoin="round"/>' +
    '<path d="M38.5 21.5 49 17" stroke="#2F4216" stroke-width="1.2" stroke-linecap="round"/>' +
    '<ellipse cx="29" cy="36" rx="10" ry="13" transform="rotate(-28 29 36)" fill="#2F4216" stroke="#F0F7D4" stroke-width="2"/>' +
    '<ellipse cx="25.5" cy="31.5" rx="2.6" ry="4.2" transform="rotate(-28 25.5 31.5)" fill="#F0F7D4" fill-opacity=".55"/>',
  tomato:
    '<path d="M32 19.5l3.2 7.6 8.2.7-6.2 5.4 1.9 8-7.1-4.3-7.1 4.3 1.9-8-6.2-5.4 8.2-.7z" fill="#22A045" stroke="#FFFFFF" stroke-width="2" stroke-linejoin="round"/>' +
    '<path d="M32 21v-6.5" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>' +
    '<path d="M32 21v-6.5" stroke="#22A045" stroke-width="1.4" stroke-linecap="round"/>',
  sea:
    '<g fill="none" stroke="#FFFFFF" stroke-width="3.2" stroke-linecap="round">' +
    '<path d="M16 27c3-3.5 5-3.5 8 0s5 3.5 8 0 5-3.5 8 0 5 3.5 8 0"/>' +
    '<path d="M16 36c3-3.5 5-3.5 8 0s5 3.5 8 0 5-3.5 8 0 5 3.5 8 0"/>' +
    '<path d="M21 45c3-3 5-3 7.5 0s5 3 7.5 0 5-3 7.5 0" stroke-opacity=".75"/></g>',
  fig:
    '<path d="M32 17c2.2 0 3 2.6 3 4.4 6.4 3 10.4 9 10.4 15 0 7.6-6 12.6-13.4 12.6S18.6 44 18.6 36.4c0-6 4-12 10.4-15 0-1.8.8-4.4 3-4.4z" fill="#FCE7F3" stroke="#FFFFFF" stroke-width="2"/>' +
    '<g fill="#9D174D"><circle cx="28" cy="35" r="1.4"/><circle cx="35" cy="33" r="1.4"/><circle cx="32" cy="40" r="1.4"/>' +
    '<circle cx="38" cy="40" r="1.4"/><circle cx="26" cy="41" r="1.4"/><circle cx="31" cy="29" r="1.4"/></g>',
  burrata:
    '<path d="M24 28c-6 3-8.5 8-8.5 11.5C15.5 46 23 50 32 50s16.5-4 16.5-10.5C48.5 36 46 31 40 28" fill="#FFFFFF" stroke="#8C7A5B" stroke-width="2"/>' +
    '<path d="M24 28c2.5-1.2 5-1.6 8-1.6s5.5.4 8 1.6l-2.6-8.6c-1.6.8-3.4 1.2-5.4 1.2s-3.8-.4-5.4-1.2z" fill="#FFFFFF" stroke="#8C7A5B" stroke-width="2" stroke-linejoin="round"/>' +
    '<path d="M24.5 27.5c4.6 1.6 10.4 1.6 15 0" stroke="#4D7C0F" stroke-width="3.2" stroke-linecap="round" fill="none"/>' +
    '<path d="M32 28.5c-2 3.5-4.6 5-7 5.8M32 28.5c2 3.5 4.6 5 7 5.8" stroke="#4D7C0F" stroke-width="2.2" stroke-linecap="round" fill="none"/>',
};

/** L'SVG intero di una bolla (64×64, disegnata grande: si rimpicciolisce bene). */
export function flavorSvg(f: Flavor): string {
  const c = FLAVOR_STYLE[f];
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="160" height="160">' +
    '<defs><radialGradient id="g" cx=".36" cy=".3" r=".78">' +
    `<stop offset="0" stop-color="${c.light}"/><stop offset=".45" stop-color="${c.base}"/><stop offset="1" stop-color="${c.dark}"/>` +
    '</radialGradient></defs>' +
    `<circle cx="32" cy="32" r="30.5" fill="url(#g)" stroke="${c.dark}" stroke-width="2"/>` +
    ICONS[f] +
    '<ellipse cx="21" cy="15.5" rx="9" ry="5" transform="rotate(-30 21 15.5)" fill="#FFFFFF" fill-opacity=".5"/>' +
    '</svg>'
  );
}

const uriCache = new Map<Flavor, string>();
export function flavorUri(f: Flavor): string {
  let uri = uriCache.get(f);
  if (!uri) {
    uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(flavorSvg(f))}`;
    uriCache.set(f, uri);
  }
  return uri;
}

/** Una bolla nella pagina (decorativa: il nome lo dice il testo accanto, se serve). */
export const FlavorBubble: React.FC<{ flavor: Flavor; className?: string; style?: React.CSSProperties }> = ({ flavor, className, style }) => (
  <img src={flavorUri(flavor)} alt="" aria-hidden="true" draggable={false} className={className} style={style} />
);

/** Il gozzo di Mola visto di fianco (bianco, fascia azzurra e rossa), per card e testata. */
export const GozzoArt: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 120 52" className={className} aria-hidden="true">
    <path d="M60 4v26" stroke="#7C4A1E" strokeWidth="3" strokeLinecap="round" />
    <path d="M62 7c12 4 20 12 22 22H62z" fill="#FFF7E6" stroke="#E7D5B0" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M4 26c10 2 30 3 56 3s46-1 56-3c-3 12-12 20-24 22H28C16 46 7 38 4 26z" fill="#FFFFFF" stroke="#0E4A63" strokeWidth="2" strokeLinejoin="round" />
    <path d="M7 32c14 2 32 3 53 3s39-1 53-3" stroke="#0EA5E9" strokeWidth="4" fill="none" />
    <path d="M10 38c13 1.6 30 2.4 50 2.4S97 39.6 110 38" stroke="#DC2626" strokeWidth="2.4" fill="none" />
    <circle cx="22" cy="31" r="2.4" fill="#0E4A63" />
  </svg>
);
